import React from 'react';

import { bool, element, number, oneOf, shape, string } from 'prop-types';

import ClassNames from 'classnames';

import BaseComponent from '../.internal/BaseComponent';
import Trigger from '../.internal/trigger/Trigger';

import './Tooltip.scss';

const POSITIONS = [
  'top',
  'top-left',
  'top-right',
  'bottom',
  'bottom-left',
  'bottom-right',
  'right',
  'right-top',
  'right-bottom',
  'left',
  'left-top',
  'left-bottom'
];

const TooltipInner = ({ content }) => {
  return (
    <>
      <div className="tooltip-arrow" />
      <div className="tooltip-inner">{content}</div>
    </>
  );
};

TooltipInner.propTypes = {
  content: string
};

TooltipInner.defaultProps = {
  content: ''
};

const classNamesByColor = {
  accent: 'PaymongoTooltip_color_accent'
};

export default class Tooltip extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      viewportWidth:
        typeof window !== 'undefined' ? window.innerWidth : undefined,
      currentTrigger: 'hover',
      hasResizeListener: false
    };
  }

  componentDidMount() {
    this.toggleResizeListener();
  }

  componentDidUpdate(prevProps) {
    const { trigger } = this.props;

    if (trigger !== prevProps.trigger) {
      this.toggleResizeListener();
    }
  }

  getPopupElement = () => {
    return <TooltipInner content={this.props.content} />;
  };

  determineTrigger = () => {
    const { trigger } = this.props;
    const { viewportWidth } = this.state;

    if (trigger === 'hover') {
      if (viewportWidth < 768) {
        return 'click';
      }

      return 'hover';
    }

    return trigger;
  };

  toggleResizeListener = () => {
    const { trigger } = this.props;
    const { hasResizeListener } = this.state;
    const innerWidth = typeof window !== 'undefined' && window.innerWidth;

    if (trigger === 'hover') {
      if (hasResizeListener) {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', this.setViewportWidth);
        }

        this.setState({ hasResizeListener: false });
      } else {
        if (typeof window !== 'undefined') {
          window.addEventListener('resize', this.setViewportWidth);
        }

        this.setState({
          hasResizeListener: true,
          viewportWidth: innerWidth
        });
      }
    } else {
      this.setState({ hasResizeListener: false });
    }
  };

  setViewportWidth = () => {
    const innerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

    this.setState({ viewportWidth: innerWidth });
  };

  componentWillUnmount() {
    this.toggleResizeListener();
  }

  render() {
    const {
      children,
      className,
      color,
      id,
      invert,
      mouseEnterDelay,
      position,
      style
    } = this.props;

    const action = this.determineTrigger();

    return (
      <Trigger
        id={id}
        action={action}
        autoAdjust
        overlayClassName={ClassNames([
          className,
          'PaymongoTooltip',
          invert && 'PaymongoTooltip_inverted',
          classNamesByColor[color]
        ])}
        overlayStyle={{ ...style }}
        popup={this.getPopupElement()}
        position={position}
        mouseEnterDelay={mouseEnterDelay}
      >
        {children}
      </Trigger>
    );
  }
}

Tooltip.propTypes = {
  children: element.isRequired,
  className: string,
  color: oneOf(['accent', 'default']),
  content: string,
  id: string,
  invert: bool,
  position: oneOf(POSITIONS),
  mouseEnterDelay: number,
  style: shape({}),
  trigger: oneOf(['click', 'hover'])
};

Tooltip.defaultProps = {
  children: null,
  className: undefined,
  content: '',
  id: undefined,
  position: 'top-right',
  style: {},
  trigger: 'hover',
  mouseEnterDelay: 0
};
